import { type GroupedSuggestionConfig, getGroupedSuggestionLabelByValue } from "./groupedSuggestion";

export const filterQueryParamKeys = {
    Region: "region",
    Sektor: "sektor",
    Støtteobjekt: "stotteobjekt",
    Støtteprogram: "stotteprogram",
    Status: "status",
    Avtaleperiode: "avtaleperiode",
} as const;

export type FilterKey = keyof typeof filterQueryParamKeys;

export type FilterDefinition = {
    queryKey: (typeof filterQueryParamKeys)[FilterKey];
    groupedConfig?: GroupedSuggestionConfig;
};

function sortGroupedConfigAlphabetically(config: GroupedSuggestionConfig): GroupedSuggestionConfig {
    return {
        ...config,
        groups: [...config.groups]
            .map((group) => ({
                ...group,
                children: [...group.children].sort((a, b) => a.label.localeCompare(b.label, "nb")),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, "nb")),
        standaloneOptions: config.standaloneOptions
            ? [...config.standaloneOptions].sort((a, b) => a.label.localeCompare(b.label, "nb"))
            : undefined,
    };
}

export const filterKeys = Object.keys(filterQueryParamKeys) as FilterKey[];

export const statusOptionOrder = ["Innvilget", "Under gjennomføring", "Ferdigstilt", "Kansellert"] as const;

export function normalizeStatusValue(value: string): string {
    return value === "Prosjektgjennomføring" ? "Under gjennomføring" : value;
}

export const regionSuggestionConfig: GroupedSuggestionConfig = {
    groups: [
        {
            label: "Oslo",
            value: "fylke-oslo",
            children: [
                { label: "Oslo", value: "kommune-oslo" },
                { label: "Bydel Grünerløkka", value: "kommune-oslo-grunerlokka" },
            ],
        },
        {
            label: "Rogaland",
            value: "fylke-rogaland",
            children: [
                { label: "Stavanger", value: "kommune-stavanger" },
                { label: "Haugesund", value: "kommune-haugesund" },
            ],
        },
        {
            label: "Møre og Romsdal",
            value: "fylke-more-og-romsdal",
            children: [
                { label: "Ålesund", value: "kommune-alesund" },
                { label: "Molde", value: "kommune-molde" },
            ],
        },
        {
            label: "Nordland",
            value: "fylke-nordland",
            children: [
                { label: "Bodø", value: "kommune-bodo" },
                { label: "Narvik", value: "kommune-narvik" },
            ],
        },
        {
            label: "Østfold",
            value: "fylke-ostfold",
            children: [
                { label: "Fredrikstad", value: "kommune-fredrikstad" },
                { label: "Sarpsborg", value: "kommune-sarpsborg" },
            ],
        },
        {
            label: "Akershus",
            value: "fylke-akershus",
            children: [
                { label: "Lillestrøm", value: "kommune-lillestrom" },
                { label: "Bærum", value: "kommune-baerum" },
            ],
        },
        {
            label: "Buskerud",
            value: "fylke-buskerud",
            children: [
                { label: "Drammen", value: "kommune-drammen" },
                { label: "Kongsberg", value: "kommune-kongsberg" },
            ],
        },
        {
            label: "Innlandet",
            value: "fylke-innlandet",
            children: [
                { label: "Hamar", value: "kommune-hamar" },
                { label: "Lillehammer", value: "kommune-lillehammer" },
            ],
        },
        {
            label: "Vestfold",
            value: "fylke-vestfold",
            children: [
                { label: "Tønsberg", value: "kommune-tonsberg" },
                { label: "Sandefjord", value: "kommune-sandefjord" },
            ],
        },
        {
            label: "Telemark",
            value: "fylke-telemark",
            children: [
                { label: "Skien", value: "kommune-skien" },
                { label: "Porsgrunn", value: "kommune-porsgrunn" },
            ],
        },
        {
            label: "Agder",
            value: "fylke-agder",
            children: [
                { label: "Kristiansand", value: "kommune-kristiansand" },
                { label: "Arendal", value: "kommune-arendal" },
            ],
        },
        {
            label: "Vestland",
            value: "fylke-vestland",
            children: [
                { label: "Bergen", value: "kommune-bergen" },
                { label: "Sogndal", value: "kommune-sogndal" },
            ],
        },
        {
            label: "Trøndelag",
            value: "fylke-trondelag",
            children: [
                { label: "Trondheim", value: "kommune-trondheim" },
                { label: "Steinkjer", value: "kommune-steinkjer" },
            ],
        },
        {
            label: "Troms",
            value: "fylke-troms",
            children: [
                { label: "Tromsø", value: "kommune-tromso" },
                { label: "Harstad", value: "kommune-harstad" },
            ],
        },
        {
            label: "Finnmark",
            value: "fylke-finnmark",
            children: [
                { label: "Alta", value: "kommune-alta" },
                { label: "Hammerfest", value: "kommune-hammerfest" },
            ],
        },
    ],
};

export const sortedRegionSuggestionConfig = sortGroupedConfigAlphabetically(regionSuggestionConfig);

export const filterDefinitions: Record<FilterKey, FilterDefinition> = {
    Region: {
        queryKey: filterQueryParamKeys.Region,
        groupedConfig: sortedRegionSuggestionConfig,
    },
    Sektor: { queryKey: filterQueryParamKeys.Sektor },
    Støtteobjekt: { queryKey: filterQueryParamKeys.Støtteobjekt },
    Støtteprogram: { queryKey: filterQueryParamKeys.Støtteprogram },
    Status: { queryKey: filterQueryParamKeys.Status },
    Avtaleperiode: { queryKey: filterQueryParamKeys.Avtaleperiode },
};

export const groupedFilterLabelByValueByKey: Partial<Record<FilterKey, Map<string, string>>> = {
    Region: getGroupedSuggestionLabelByValue(sortedRegionSuggestionConfig),
};
